import { Check } from 'lucide-react'
import { Button } from '@/components/ui'
import { SeverityBadge, severityIcon, severityTone } from '@/components/hearth'
import { cn, formatDateTime, formatRelativeTime } from '@/lib/utils'
import type { Alert } from '@/types'

interface AlertCardProps {
  alert: Alert
  /** Whether the card is expanded to show full detail */
  expanded: boolean
  /** Deep-linked via ?id= — gets a gentle highlight ring */
  highlighted?: boolean
  /** Entrance stagger index for hearth-appear */
  stagger?: number
  onToggle: () => void
  onAcknowledge: () => void
  onResolveRequest: () => void
  isAcknowledging?: boolean
  isResolving?: boolean
}

/**
 * Hearth alert card mirroring the iOS AlertsListView row: severity chip,
 * two-line title/description, relative time, and a calm inline action.
 * The whole card is tappable to expand the alert's detail.
 */
export function AlertCard({
  alert,
  expanded,
  highlighted = false,
  stagger = 0,
  onToggle,
  onAcknowledge,
  onResolveRequest,
  isAcknowledging = false,
  isResolving = false,
}: AlertCardProps) {
  const tone = severityTone[alert.severity]
  const Icon = severityIcon[alert.severity]

  const isUnread = alert.status === 'UNREAD'
  const isResolved = alert.status === 'RESOLVED'

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onToggle()
    }
  }

  return (
    <div
      id={`alert-${alert.id}`}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onClick={onToggle}
      onKeyDown={handleKeyDown}
      className={cn(
        'hearth-card hearth-press hearth-appear relative cursor-pointer overflow-hidden p-4',
        isUnread && tone.wash,
        highlighted && 'ring-2 ring-primary-400'
      )}
      style={{ '--stagger': stagger } as React.CSSProperties}
    >
      {isUnread && (
        <span
          aria-hidden
          className={cn('absolute left-0 top-0 h-full w-1 rounded-r-full', tone.dot)}
        />
      )}

      <div className="flex items-start gap-3">
        {/* Severity chip */}
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
            tone.chipBg
          )}
        >
          <Icon className={cn('h-5 w-5', tone.fg)} />
        </div>

        <div className="min-w-0 flex-1">
          <p className={cn('font-medium text-gray-900', !expanded && 'line-clamp-2')}>
            {alert.title}
          </p>
          <p className={cn('mt-0.5 text-sm text-gray-500', !expanded && 'line-clamp-2')}>
            {alert.description}
          </p>

          {expanded && (
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
              <SeverityBadge severity={alert.severity} />
              <span className="text-xs text-gray-400">{formatDateTime(alert.created_at)}</span>
              {!isResolved && (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={isResolving}
                  onClick={(event) => {
                    event.stopPropagation()
                    onResolveRequest()
                  }}
                >
                  Mark resolved
                </Button>
              )}
            </div>
          )}

          {/* Footer row */}
          <div className="mt-3 flex items-center justify-between gap-3">
            <span className="text-xs text-gray-400">
              {formatRelativeTime(alert.created_at)}
            </span>

            {isUnread && (
              <button
                type="button"
                disabled={isAcknowledging}
                onClick={(event) => {
                  event.stopPropagation()
                  onAcknowledge()
                }}
                className="rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100 disabled:pointer-events-none disabled:opacity-50"
              >
                Got it
              </button>
            )}

            {isResolved && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-success-600">
                <Check className="h-3.5 w-3.5" />
                Resolved
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
